<template>
    <MobileModal
        :is-open="ui.shareCollectionModal.opened"
        :title="modalTitle"
        max-width="420px"
        max-height="80vh"
        :z-index="999"
        :container-class="{ 'max-h-full max-w-full rounded-none': onlyMobileVisible }"
        @close="closeShareCollectionModal"
    >
        <ShareLinkGenerated v-if="generatedUrl" :url="generatedUrl" />

        <template v-else>
            <div class="px-5 py-4">
                <p class="mb-4 mt-0 text-sm leading-relaxed text-theme-text">
                    {{ modalDescription }}
                </p>

                <div v-if="colorsInUse.length > 0" class="flex flex-wrap items-center gap-2 py-1">
                    <div
                        v-for="colorEntry in colorsInUse"
                        :key="colorEntry.slug"
                        v-tooltip.top-start="colorEntry.label"
                        class="flex items-center"
                    >
                        <input
                            :id="'shareColor-' + colorEntry.slug"
                            :checked="selectedColorSlug === colorEntry.slug"
                            :value="colorEntry.slug"
                            :aria-label="colorEntry.label"
                            type="radio"
                            name="shareCollectionColor"
                            class="peer sr-only"
                            @change="selectedColorSlug = colorEntry.slug"
                        />
                        <ColorSwatch
                            :for-id="'shareColor-' + colorEntry.slug"
                            :color="colorEntry.color"
                            :is-custom="true"
                            :is-selected="selectedColorSlug === colorEntry.slug"
                            class="tap-target peer-focus-visible:ring-2 peer-focus-visible:ring-theme-accent peer-focus-visible:ring-offset-2"
                        />
                    </div>
                </div>
                <p v-else class="py-6 text-center text-[13px] text-theme-icon">
                    {{ emptyStateLabel }}
                </p>
            </div>

            <div class="flex-shrink-0 border-t border-theme-border px-4 py-3">
                <button
                    type="button"
                    class="w-full rounded-lg bg-theme-buynow-bg px-2.5 py-2.5 text-sm font-semibold text-theme-buynow-text transition-opacity hover:opacity-85 disabled:cursor-not-allowed disabled:opacity-40"
                    :disabled="!selectedColorSlug || isCreatingLink || isOffline"
                    @click="generateShareLink"
                >
                    {{ copyLinkLabel }}
                </button>
            </div>
        </template>
    </MobileModal>
</template>

<script>
'use strict';

import * as Sentry from '@sentry/browser';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import MobileModal from '../Components/MobileModal.vue';
import ShareLinkGenerated from './ShareLinkGenerated.vue';
import ColorSwatch from '../Components/ColorSwatch.vue';
import { notesAPI } from '../../../src/js/communication';
import { ALL_PALETTE } from '../../js/constants/highlightColors';

/**
 * Built-in palette slugs (default + extended). Highlighters whose slug
 * matches one of these are predefined by the system and not eligible for
 * sharing — only user-created highlighters can be shared.
 */
const BUILT_IN_PALETTE_SLUGS = new Set(ALL_PALETTE.map(color => color.slug));

export default {
    components: { MobileModal, ShareLinkGenerated, ColorSwatch },
    data() {
        return {
            selectedColorSlug: null,
            isCreatingLink: false,
            generatedUrl: '',
        };
    },
    computed: {
        ...mapState(['ui', 'localizations', 'lang', 'issue', 'isOffline']),
        ...mapGetters(['onlyMobileVisible', 'allHighlighters', 'usedColorSlugs']),
        /**
         * Highlighters available for sharing in the current book.
         *
         * Must satisfy BOTH conditions:
         *   1. The user actually used the color in this book.
         *   2. The color is user-created — its slug is NOT in
         *      {@link BUILT_IN_PALETTE_SLUGS}. Built-in palette entries
         *      (default + extended) are not shareable.
         */
        colorsInUse() {
            const usedSlugs = new Set(this.usedColorSlugs);
            return this.allHighlighters.filter(
                color => usedSlugs.has(color.slug) && !BUILT_IN_PALETTE_SLUGS.has(color.slug)
            );
        },
        modalTitle() {
            return this.localizations[this.lang].sharing?.shareCollection || 'Share collection';
        },
        modalDescription() {
            return (
                this.localizations[this.lang].sharing?.shareWithOthersDescription ||
                'Share your highlights with other users. Pick a collection. Others will follow your highlights as you add new ones.'
            );
        },
        /**
         * Returns the empty-state message shown when no shareable colors exist.
         * Distinguishes between "no highlights at all" and "highlights exist but
         * none are shareable" (only custom colors are shareable; built-in are not).
         */
        emptyStateLabel() {
            const langSharing = this.localizations[this.lang]?.sharing;
            const enSharing = this.localizations.en?.sharing;
            const hasAnyUsedColor = this.usedColorSlugs.length > 0;

            if (hasAnyUsedColor) {
                return langSharing?.noShareableColors || enSharing?.noShareableColors;
            }
            return langSharing?.noHighlightsToShare || enSharing?.noHighlightsToShare;
        },
        copyLinkLabel() {
            return this.localizations[this.lang].sharing?.copyLink || 'Copy link';
        },
    },
    watch: {
        'ui.shareCollectionModal.opened'(isOpened) {
            if (isOpened) {
                this.resetModalState();
            }
        },
    },
    methods: {
        ...mapMutations(['closeShareCollectionModal', 'openToast']),
        ...mapActions(['copyToClipBoard']),
        /**
         * Resets modal to its initial state when opened.
         */
        resetModalState() {
            this.selectedColorSlug = null;
            this.isCreatingLink = false;
            this.generatedUrl = '';
        },
        /**
         * Generates a share link for the selected color via the notes API.
         */
        async generateShareLink() {
            if (!this.selectedColorSlug || this.isCreatingLink || this.isOffline) return;

            this.isCreatingLink = true;
            try {
                const response = await notesAPI.createShareLink(this.selectedColorSlug, this.issue.id);
                await this.copyToClipBoard(response.url);
                this.generatedUrl = response.url;
            } catch (error) {
                Sentry.captureException(error);
                console.error('[ShareCollectionModal] Failed to create share link:', error);
                const fallback =
                    this.localizations[this.lang]?.sharing?.shareLinkFailed ||
                    this.localizations.en?.sharing?.shareLinkFailed;
                this.openToast({ text: error?.message || fallback });
            } finally {
                this.isCreatingLink = false;
            }
        },
    },
};
</script>
